"use client";

import { JSX, useMemo, useState } from "react";
import { SquareArrowOutUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { CascadeIn } from "@/components/motion/cascade-in";
import { VisualRefRender } from "@/components/handyout-components/visual-ref";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import type {
  KeyTakeawaysVisual,
  ModuleKeyTakeawaysData,
  ModuleKeyTakeawaysItem,
} from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useAudienceSession } from "@/contexts/audience-session-context";
import { emitModuleInteraction } from "@/lib/module-interaction";

const ROWS = 2;

function comparePositions(a: string, b: string): number {
  const as = a.split(".").map(Number);
  const bs = b.split(".").map(Number);
  for (let i = 0; i < Math.max(as.length, bs.length); i++) {
    const av = as[i];
    const bv = bs[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    if (Number.isNaN(av) || Number.isNaN(bv)) return a.localeCompare(b);
    if (av !== bv) return av - bv;
  }
  return 0;
}

function resolveVisual(
  item: ModuleKeyTakeawaysItem,
  index: number,
): KeyTakeawaysVisual {
  return item.visual ?? { type: "number", value: index + 1 };
}

export default function ModuleKeyTakeaways({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleKeyTakeawaysData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element | null {
  const entries = useMemo(
    () =>
      Object.entries(data ?? {})
        .filter(([, item]) => item != null)
        .sort(([a], [b]) => comparePositions(a, b)),
    [data],
  );
  const [hovered, setHovered] = useState<string | null>(null);
  const hasHover = useHoverCapable();
  const { goToSlideNumber, hasSlideNumber } = useAudienceSession();

  if (entries.length === 0) return null;

  return (
    <div
      className={cn("w-full min-w-0", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <SwipeScroll
        className="w-full"
        fade="auto"
      >
        <CascadeIn
          keySignature="key-takeaways"
          className="grid grid-flow-col grid-rows-[repeat(2,auto)] gap-x-6 auto-cols-[minmax(15rem,20rem)]"
          itemClassName="w-full min-w-0"
        >
          {entries.map(([key, item], i) => {
            const visual = resolveVisual(item, i);
            const isHovered = hovered === key;
            const dim = hovered !== null && !isHovered;
            return (
              <div
                key={key}
                onMouseEnter={hasHover ? () => setHovered(key) : undefined}
                onMouseLeave={hasHover ? () => setHovered(null) : undefined}
                onFocus={hasHover ? () => setHovered(key) : undefined}
                onBlur={hasHover ? () => setHovered(null) : undefined}
                className={cn(
                  "grid w-full min-w-0 grid-cols-[28px_1fr] items-start gap-x-2.5 py-3 transition-opacity",
                  // Column-major flow: the separator belongs to every item that is
                  // not the first row of its own column.
                  i % ROWS !== 0 &&
                    "border-t border-dashed border-[var(--border-dashed)]",
                  dim && "opacity-40",
                )}
              >
                <span
                  className={cn(
                    "flex h-7 items-center transition-colors",
                    isHovered ? "text-[var(--fg)]" : "text-[var(--module-accent)]",
                    visual.type === "number" &&
                      cn(
                        "w-7 justify-center rounded-full border",
                        isHovered
                          ? "border-[var(--fg)]"
                          : "border-[var(--border)]",
                      ),
                  )}
                >
                  <VisualRefRender
                    visual={visual}
                    alt={stripInlineMarkdown(item.heading)}
                    className={
                      visual.type === "number" ? "text-[13px]" : "h-6 w-6"
                    }
                  />
                </span>

                <div className="min-w-0">
                  <div
                    className={cn(
                      "text-[15px] font-medium leading-snug",
                      MODULE_LABEL_WRAP.heading,
                    )}
                  >
                    {stripInlineMarkdown(item.heading)}
                  </div>
                  <p
                    className={cn(
                      "mt-1 text-sm leading-relaxed text-[var(--muted)]",
                      MODULE_LABEL_WRAP.body,
                    )}
                  >
                    {stripInlineMarkdown(item.body)}
                  </p>
                  {typeof item.slide === "number" &&
                    (hasSlideNumber(item.slide) ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          goToSlideNumber(item.slide!);
                          emitModuleInteraction(
                            e.currentTarget,
                            "click",
                            { kind: "link", id: key },
                            { targetSlide: item.slide! },
                          );
                        }}
                        className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-xs tabular-nums text-[var(--subtle)] transition-colors hover:text-[var(--fg)]"
                      >
                        <span className="underline decoration-dashed underline-offset-4">
                          Jump to slide {item.slide}
                        </span>
                        <SquareArrowOutUpRight
                          className="h-3 w-3 shrink-0"
                          aria-hidden
                        />
                      </button>
                    ) : (
                      <span className="mt-1.5 inline-block text-xs tabular-nums text-[var(--subtle)]">
                        Slide {item.slide}
                      </span>
                    ))}
                </div>
              </div>
            );
          })}
        </CascadeIn>
      </SwipeScroll>
    </div>
  );
}
